import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import Navigation from '@/components/shared/Navigation';
import { 
  Trophy, 
  Users, 
  CreditCard, 
  Shield, 
  Star,
  CheckCircle,
  ArrowRight,
  Play
} from 'lucide-react';
import { memberTypesApi } from '@/lib/api';
import { MemberType } from '@/types';
import LoadingSpinner from '@/components/shared/LoadingSpinner';
import { toast } from '@/components/ui/use-toast';

const Index = () => {
  const [memberTypes, setMemberTypes] = useState<MemberType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMemberTypes = async () => {
      try {
        setIsLoading(true);
        const response = await memberTypesApi.getAll();
        let types: MemberType[] = [];
        if (Array.isArray(response)) {
          types = response;
        } else if (response && Array.isArray(response.data)) {
          types = response.data;
        } else if (response && response.status === 'success' && Array.isArray(response.data)) {
          types = response.data;
        } else {
          console.warn('Unexpected member types response format:', response);
          types = [];
        }
        setMemberTypes(types);
      } catch (err: any) {
        console.error('Failed to fetch member types:', err);
        setError(err.message || 'Failed to load membership options.');
        toast({
          title: 'Error',
          description: 'Failed to load membership options. Please try again later.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchMemberTypes();
  }, []);

  const renderBenefits = (description: string) => {
    if (!description) return null;
    return description.split('\n').map((benefit, index) => (
      <li key={index} className="flex items-center">
        <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
        <span>{benefit.trim()}</span>
      </li>
    ));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1546519638-68e109498ffc?w=1920&h=1080&fit=crop&crop=center")',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-basketball-blue-500/90 to-basketball-orange-500/80"></div>
        </div>
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <div className="mb-6">
                <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-4">
                  <Trophy className="w-5 h-5 text-gold-500" />
                  <span className="text-sm font-medium">Tanzania's Premier Basketball Club</span>
                </div>
                <h1 className="text-4xl lg:text-6xl font-bold mb-4 text-shadow">
                  OG Basketball Club
                  <span className="block text-basketball-orange-400">Tanzania</span>
                </h1>
                <p className="text-xl lg:text-2xl opacity-90 mb-8">
                  Join the legacy. Experience premium basketball training with digital membership cards and exclusive benefits.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link to="/register">
                  <Button 
                    size="lg" 
                    className="bg-basketball-orange-500 hover:bg-basketball-orange-600 text-white px-8 py-4 text-lg font-semibold animate-glow"
                  >
                    Join the Club
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white text-white hover:bg-white hover:text-basketball-blue-500 px-8 py-4 text-lg"
                >
                  <Play className="w-5 h-5 mr-2" />
                  Watch Training
                </Button>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold">500+</div>
                  <div className="text-sm opacity-75">Active Members</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">15+</div>
                  <div className="text-sm opacity-75">Championships</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">10</div>
                  <div className="text-sm opacity-75">Years Legacy</div>
                </div>
              </div>
            </div>

            {/* Hero Image/Card Preview */}
            <div className="lg:flex lg:justify-center">
              <div className="relative">
                {/* 3D Card Preview */}
                <div className="w-80 h-48 perspective-1000">
                  <div className="w-full h-full basketball-gradient rounded-xl shadow-2xl transform rotate-6 hover:rotate-3 transition-transform duration-500">
                    <div className="p-6 h-full flex flex-col justify-between text-white relative overflow-hidden">
                      {/* Card Background Pattern */}
                      <div className="absolute inset-0 bg-court-pattern opacity-20"></div>
                      <div className="absolute bottom-0 left-0 right-0 h-20 bg-kilimanjaro-silhouette"></div>
                      
                      {/* Card Content */}
                      <div className="relative z-10">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="font-orbitron font-bold text-lg">OG BASKETBALL</h3>
                            <p className="text-sm opacity-90">Tanzania Club</p>
                          </div>
                          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                            🏀
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-3 mb-4">
                          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm border border-white/30"></div>
                          <div>
                            <div className="font-bold">MEMBER NAME</div>
                            <div className="text-sm opacity-75">Active OG</div>
                          </div>
                        </div>
                        
                        <div className="flex justify-between text-sm">
                          <span className="opacity-75">Digital Membership</span>
                          <span className="font-mono">OGB2025XXXX</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Floating Elements */}
                <div className="absolute -top-4 -right-4 w-16 h-16 bg-basketball-orange-500 rounded-full opacity-60 animate-bounce-basketball"></div>
                <div className="absolute -bottom-6 -left-6 w-12 h-12 bg-tanzania-green-500 rounded-full opacity-40 animate-bounce-basketball" style={{ animationDelay: '0.5s' }}></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Why Choose OG Basketball Club?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Experience premium basketball training with cutting-edge digital membership technology
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Feature Cards */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300 animate-fade-in-up">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-basketball-gradient rounded-full flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2">Premium Digital Cards</h3>
                <p className="text-gray-600">3D animated membership cards with QR verification and mobile wallet integration</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-basketball-gradient rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2">Professional Training</h3>
                <p className="text-gray-600">Expert coaches and structured programs for all skill levels from beginner to professional</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-basketball-gradient rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2">Secure Payments</h3>
                <p className="text-gray-600">Integrated mobile money payments with M-Pesa, Airtel, Tigo, and Halopesa support</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-basketball-gradient rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2">Exclusive Benefits</h3>
                <p className="text-gray-600">Access to tournaments, special events, and premium basketball facilities</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Membership Types */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Choose Your Membership
            </h2>
            <p className="text-xl text-gray-600">
              Flexible membership options designed for every basketball enthusiast
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {isLoading ? (
              <div className="lg:col-span-3 flex justify-center py-10">
                <LoadingSpinner />
              </div>
            ) : error ? (
              <div className="lg:col-span-3 text-center py-10 text-red-600">
                <p>{error}</p>
                <p className="mt-2">Please try refreshing the page.</p>
              </div>
            ) : memberTypes.length === 0 ? (
              <div className="lg:col-span-3 text-center py-10 text-gray-500">
                <p>No membership types available at the moment.</p>
              </div>
            ) : (
              memberTypes.map((type, index) => (
                <Card 
                  key={type.id}
                  className={`border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 ${index === 1 ? 'border-2 border-basketball-orange-500 shadow-xl relative' : ''}`}
                >
                  {index === 1 && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className="bg-basketball-orange-500 text-white px-4 py-2 rounded-full text-sm font-bold">
                        MOST POPULAR
                      </div>
                    </div>
                  )}
                  <CardContent className="p-8">
                    <div className="text-center mb-6">
                      <h3 className="text-2xl font-bold mb-2">{type.name}</h3>
                      <div className="text-4xl font-bold text-basketball-orange-500 mb-2">TSh {type.monthly_fee?.toLocaleString() || 'N/A'}</div>
                      <p className="text-gray-600">per month</p>
                    </div>
                    
                    <ul className="space-y-3 mb-8">
                      {renderBenefits(type.description)}
                    </ul>
                    
                    <Link to={`/register?type=${type.name.toLowerCase().replace(/ /g, '-')}`} className="block">
                      <Button className="w-full bg-basketball-gradient hover:opacity-90">
                        Choose {type.name}
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-basketball-gradient">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Ready to Join the OG Basketball Family?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Start your basketball journey today with instant registration and digital membership cards
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button 
                size="lg" 
                className="bg-white text-basketball-blue-500 hover:bg-gray-100 px-8 py-4 text-lg font-semibold"
              >
                Register Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/login">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white hover:text-basketball-blue-500 px-8 py-4 text-lg"
              >
                Member Login
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-basketball-blue-500 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-basketball-orange-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">🏀</span>
                </div>
                <div>
                  <div className="font-orbitron font-bold">OG Basketball</div>
                  <div className="text-sm opacity-75">Tanzania Club</div>
                </div>
              </div>
              <p className="text-sm opacity-75">
                Tanzania's premier basketball club with premium digital membership experience.
              </p>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/register" className="hover:text-basketball-orange-400">Join Club</Link></li>
                <li><Link to="/login" className="hover:text-basketball-orange-400">Member Login</Link></li>
                <li><a href="#features" className="hover:text-basketball-orange-400">Features</a></li>
                <li><a href="#membership" className="hover:text-basketball-orange-400">Membership</a></li>
                <li><Link to="/terms" className="hover:text-basketball-orange-400">Terms & Conditions</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm opacity-75">
                <li>📍 Dar es Salaam, Tanzania</li>
                <li>📞 +255 XXX XXX XXX</li>
                <li>✉️ info@ogbasketball.co.tz</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">Follow Us</h4>
              <div className="flex space-x-4">
                <a href="#" className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center hover:bg-basketball-orange-500 transition-colors">
                  f
                </a>
                <a href="#" className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center hover:bg-basketball-orange-500 transition-colors">
                  t
                </a>
                <a href="#" className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center hover:bg-basketball-orange-500 transition-colors">
                  i
                </a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-white/20 mt-8 pt-8 text-center">
            <p className="text-sm opacity-75">
              © 2025 OG Basketball Club Tanzania. All rights reserved.
            </p>
            <div className="mt-2 text-xs opacity-60">
              <Link to="/terms" className="hover:text-basketball-orange-400 mr-4">Terms & Conditions</Link>
              <span className="mr-4">|</span>
              <span>Privacy Policy</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
